import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";

export function Account() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forced = searchParams.get("forced") === "1";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!user) return null;

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must differ from the current one.");
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      // After a forced rotation, drop them onto the dashboard.
      if (forced) navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          (err.body as { detail?: string } | null)?.detail ??
          `HTTP ${err.status}`;
        setError(detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Password change failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__title">Account</div>
        <p className="auth-card__sub">
          Signed in as <span className="mono">{user.email}</span>
        </p>

        {forced && (
          <div className="auth-banner auth-banner--warn">
            Your account is using a bootstrap password. Set a new one before
            continuing.
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label className="auth-field">
            <span>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label className="auth-field">
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <label className="auth-field">
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </label>

          {error && <div className="auth-error">{error}</div>}
          {success && !forced && (
            <div className="auth-banner auth-banner--ok">
              Password updated. Other sessions have been signed out.
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary auth-submit"
            disabled={submitting || !currentPassword || !newPassword || !confirm}
          >
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>

        <div className="auth-card__footer">
          {!forced && <Link to="/">Back to dashboard</Link>}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              void logout().then(() => navigate("/login", { replace: true }));
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
